import React from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Box, 
  alpha,
  Button,
  useTheme,
  SvgIconProps,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AppShortcut as AppIcon,
  Web as WebIcon,
  PhoneIphone as MobileIcon,
  Cloud as CloudIcon,
  Support as SupportIcon,
  Palette as DesignIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';

interface ServiceCardProps {
  icon: React.ReactElement<SvgIconProps>;
  title: string;
  description: string;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, description }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        p: { xs: 3, md: 4 },
        borderRadius: '16px',
        background: `linear-gradient(145deg, ${alpha('#1a1a1a', 0.4)} 0%, ${alpha('#0a0a0a', 0.4)} 100%)`,
        border: '1px solid rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: `0 10px 25px ${alpha(theme.palette.primary.main, 0.2)}`,
          '& .arrow-icon': {
            transform: 'translateX(5px)',
          }
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3,
          p: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderRadius: '12px',
          width: 70,
          height: 70,
        }}
      >
        {React.cloneElement(icon, {
          sx: { fontSize: 38, color: 'primary.main' }
        })}
      </Box>
      <Typography 
        variant="h6" 
        component="h3" 
        gutterBottom
        sx={{ 
          fontWeight: 600,
          mb: 1.5,
          width: '100%'
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="body2" 
        color="text.secondary"
        sx={{ 
          width: '100%',
          flexGrow: 1,
          mb: 3
        }}
      >
        {description}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 'auto',
          color: theme.palette.primary.main,
          fontWeight: 600,
          fontSize: '0.875rem'
        }}
      >
        {t('services.readMore')}
        <ArrowIcon className="arrow-icon" sx={{ ml: 1, fontSize: '1rem', transition: 'transform 0.2s ease-in-out' }} />
      </Box>
    </Paper>
  );
};

const ServicesSection: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const services = [
    {
      icon: <AppIcon />,
      title: t('services.appDevelopment.title'),
      description: t('services.appDevelopment.shortDescription'),
    },
    {
      icon: <WebIcon />,
      title: t('services.webDevelopment.title'),
      description: t('services.webDevelopment.shortDescription'),
    },
    {
      icon: <MobileIcon />,
      title: t('services.mobileDevelopment.title'),
      description: t('services.mobileDevelopment.shortDescription'),
    },
    {
      icon: <CloudIcon />,
      title: t('services.cloudServices.title'),
      description: t('services.cloudServices.shortDescription'),
    },
    {
      icon: <SupportIcon />,
      title: t('services.technicalSupport.title'),
      description: t('services.technicalSupport.shortDescription'),
    },
    {
      icon: <DesignIcon />,
      title: t('services.uiUxDesign.title'),
      description: t('services.uiUxDesign.shortDescription'),
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, md: 12 },
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: 'radial-gradient(circle at top left, rgba(128,255,128,0.05) 0%, transparent 70%)',
          zIndex: 0,
        },
      }}
    >
      <Container 
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 6, md: 8 },
            maxWidth: '800px',
            width: '100%',
            mx: 'auto'
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 2.5
            }}
          >
            {t('services.title')}
          </Typography>
          <Typography
            variant="h6"
            component="p"
            color="text.secondary"
            sx={{ 
              maxWidth: '650px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.125rem' },
              px: { xs: 1, md: 0 },
            }}
          >
            {t('services.subtitle')}
          </Typography>
        </Box>

        <Box 
          sx={{ 
            width: '100%', 
            mb: 6,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Grid 
            container 
            spacing={3}
            justifyContent="center"
            maxWidth="1200px"
          >
            {services.map((service, index) => (
              <Grid 
                key={index}
                size={{ xs: 12, sm: 6, md: 4 }}
              >
                <Box 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    justifyContent: 'center'
                  }}
                >
                  <ServiceCard
                    icon={service.icon}
                    title={service.title}
                    description={service.description}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
        
        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <Button
            component={Link}
            to="/services"
            variant="contained"
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
              }
            }}
          >
            {t('services.viewAllButton')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesSection; 